import { useState } from 'react'
import { Modal } from '../ui/Modal'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import type { GeneratedImage } from '../../lib/api'
import { getImageAssetUrl, editVisualImage, ApiError } from '../../lib/api'
import { toast } from 'sonner'
import { ImageAnnotationTool } from './ImageAnnotationTool'

interface EditImageModalProps {
  isOpen: boolean
  onClose: () => void
  image: GeneratedImage
  contentId?: string | null
  visualTaskId?: string | null
  onSuccess?: () => void
}

export function EditImageModal({
  isOpen,
  onClose,
  image,
  contentId,
  visualTaskId,
  onSuccess,
}: EditImageModalProps) {
  const [instruction, setInstruction] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [useMask, setUseMask] = useState(false)
  const [maskDataUrl, setMaskDataUrl] = useState<string | null>(null)

  const imageUrl = image.id ? getImageAssetUrl(image.id) : image.public_url ?? ''
  const imageFallbackUrl = image.public_url ?? null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = instruction.trim()
    if (!trimmed) return
    setIsSubmitting(true)
    try {
      await editVisualImage({
        source_image_id: image.id,
        instruction: trimmed,
        content_id: contentId ?? null,
        visual_task_id: visualTaskId ?? null,
        ...(useMask && maskDataUrl ? { mask_data_url: maskDataUrl } : {}),
      })
      toast.success('Edición enviada', {
        description: 'La nueva imagen aparecerá en la galería cuando esté lista.',
      })
      setInstruction('')
      onSuccess?.()
      onClose()
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error('Error al editar la imagen', {
          description: err.message,
        })
      } else {
        toast.error('Error inesperado', {
          description: err instanceof Error ? err.message : 'No se pudo enviar la edición',
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Editar imagen con IA" size="lg">
      <div className="space-y-4">
        <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center min-h-[200px]">
          {imageUrl ? (
            useMask ? (
              <ImageAnnotationTool
                imageUrl={imageUrl}
                imageUrlFallback={imageFallbackUrl}
                onMaskChange={setMaskDataUrl}
                className="w-full p-2"
              />
            ) : (
              <img
                src={imageUrl}
                alt="Imagen a editar"
                className="max-w-full max-h-[40vh] object-contain"
                onError={(e) => {
                  if (imageFallbackUrl) {
                    ;(e.target as HTMLImageElement).src = imageFallbackUrl
                  }
                }}
              />
            )
          ) : (
            <p className="text-slate-500 text-sm">Imagen no disponible</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            id="edit-use-mask"
            type="checkbox"
            checked={useMask}
            onChange={(e) => {
              setUseMask(e.target.checked)
              if (!e.target.checked) setMaskDataUrl(null)
            }}
            className="rounded border-slate-300 text-slate-700 focus:ring-slate-500"
          />
          <label htmlFor="edit-use-mask" className="text-sm text-slate-600">
            Usar máscara (marcar área a editar)
          </label>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="edit-instruction" className="label">
              Instrucciones de edición *
            </label>
            <textarea
              id="edit-instruction"
              className="input min-h-[100px]"
              rows={4}
              placeholder="Describe los cambios que quieres aplicar a la imagen..."
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              required
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!instruction.trim() || isSubmitting}
              className="btn btn-primary flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" />
                  Enviando...
                </>
              ) : (
                'Editar con IA'
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
}
